import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import AmountField from "../../Components/Form Fields/amount-field";
import CheckBox from "../../Components/Form Fields/checkbox";
import PrimaryButton from "../../Components/Buttons/primary-button";

import renderAuthorizeVenmoTransfer from "../../Organisms/Modals/Transfer/authorize-venmo-transfer";

import { useActivities, useDeals, useUserAccount } from "../../CustomHooks/Queries";

import withFormProvider from "../../HOC/withFormProvider";

import { useModalContext } from "../../Contexts/ModalContext";

import { dispatchErrorToast, formatCurrency, transformCountryCodeFormatToUSNumber } from "../../Utils/helpers";

import { VenmoLogo } from "../../Assets/Images";
import { ArrowLeftCircle, Wallet } from "../../Assets/Icons";

function VenmoTransfer() {
  const modalContext = useModalContext();

  const navigate = useNavigate();

  const { watch, register, handleSubmit } = useFormContext();

  const { isLoading: isLoadingDeals, data: deals } = useDeals();

  const { isLoading: isLoadingActivities, data: activitiesData } = useActivities();

  const { isLoading: isLoadingUserAccountData, data: userAccountData } = useUserAccount();

  const dailyCashOutLimit = useMemo(() => deals?.data?.content?.transfer_out_limit, [deals]);

  const phoneNumber = useMemo(
    () => transformCountryCodeFormatToUSNumber(userAccountData?.data?.data?.user?.phoneNumber ?? ""),
    [userAccountData]
  );

  const cashAmount = parseFloat(watch("cash-amount")?.unmasked ?? "");
  const agreeToTerms = watch("agreeToTerms") ?? false;

  const disableSubmitButton = isNaN(cashAmount) || cashAmount === 0 || !agreeToTerms;

  const onSubmit = () => {
    if (dailyCashOutLimit > 0 && cashAmount > dailyCashOutLimit) {
      dispatchErrorToast("Amount cannot exceed the daily limit.", {
        alignToAppContent: true,
        key: "amountExceedsDailyLimit",
      });
    } else if (cashAmount > (activitiesData?.data?.data?.currentBalance ?? 0)) {
      dispatchErrorToast("Amount exceeds what you have available to transfer.", {
        alignToAppContent: true,
        key: "amountExceedsAvailableFunds",
      });
    } else {
      renderAuthorizeVenmoTransfer(modalContext, { cashAmount });
    }
  };

  const isLoading = isLoadingDeals || isLoadingActivities || isLoadingUserAccountData;

  return (
    <div className="venmo-transfer">
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <div className="section-header">
              <button className="go-back-button" onClick={navigate.bind(this, -1)}>
                <ArrowLeftCircle />
              </button>
              <h3 className="section-title">Venmo Transfer</h3>
              <div className="dummy"></div>
            </div>
            {isLoading ? (
              <div className="loading-content">
                <CircularProgress />
              </div>
            ) : (
              <form className="content mt-sm-12 mt-6" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <div className="top-container">
                  <AmountField />
                  {!!dailyCashOutLimit && (
                    <span className="amount-usage-label mt-2">Daily limit: {formatCurrency(dailyCashOutLimit)}</span>
                  )}
                </div>
                <div className="bottom-container">
                  <div className="details">
                    <div className="detail-row">
                      <span className="detail-label">From</span>
                      <div className="payee-card">
                        <div className="account-badge">
                          <Wallet />
                        </div>
                        <div className="info-container">
                          <p className="title">Stake Wallet</p>
                          <p className="description">
                            {formatCurrency(activitiesData?.data?.data?.currentBalance ?? 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">To</span>
                      <div className="payee-card">
                        <div className="account-badge">
                          <VenmoLogo className="venmo-logo filled" />
                        </div>
                        <div className="info-container">
                          <p className="title">Venmo</p>
                          <p className="description">{phoneNumber}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ackowledgement-message">
                    <CheckBox {...register("agreeToTerms", { required: true })} checked={agreeToTerms} />I understand
                    this transfer will be sent to the Venmo account associated with {phoneNumber}.
                  </div>
                  <PrimaryButton className="mt-sm-6 mt-4" type="submit" disabled={disableSubmitButton}>
                    Transfer
                  </PrimaryButton>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default withFormProvider(VenmoTransfer);
